import React from 'react';

/**
 * AmbientCanvas — the atmosphere every surface floats on.
 *
 * The shell used to be one card on a flat near-black body. Split into peer
 * surfaces, that flat colour reads as dead space: the gap between panes has
 * nothing in it. So the background becomes a real canvas.
 *
 * The mesh is built from --primary and --accent, which the project Layout
 * already derives from the project's icon via ColorThief. That means the
 * ambience re-tints per project with no extra wiring — each workspace glows in
 * its own colour, and the gap between surfaces becomes the most expressive part
 * of the screen. It also means a stale palette is very visible, which is why
 * Layout resets those vars on unmount and when a project has no icon.
 *
 * Nothing here animates. A full-viewport gradient animation forces continuous
 * repaint underneath backdrop-blurred surfaces, which is exactly the worst case
 * for this composition; static already reads as atmosphere.
 */

// Module scope so these objects aren't reallocated on every render.

const MESH = {
    backgroundImage: [
        // Anchored to opposite corners so the inter-surface gap always crosses
        // a colour ramp rather than sitting in a flat patch.
        // Core pushed well off-canvas so the nav rail sits in the falloff, not
        // the hot centre — a highly saturated project icon would otherwise wash
        // out the nav labels.
        'radial-gradient(70rem 48rem at 6% -18%, hsl(var(--primary) / 0.34), transparent 62%)',
        'radial-gradient(50rem 38rem at 95% 4%, hsl(var(--accent) / 0.26), transparent 64%)',
        'radial-gradient(55rem 46rem at 70% 110%, hsl(var(--primary) / 0.20), transparent 66%)',
    ].join(', '),
};

const GRID = {
    backgroundImage:
        'linear-gradient(to right, hsl(0 0% 100% / 0.07) 1px, transparent 1px),' +
        'linear-gradient(to bottom, hsl(0 0% 100% / 0.07) 1px, transparent 1px)',
    backgroundSize: '48px 48px',
    // Fades the grid out toward the edges so it suggests a surface without
    // turning into graph paper.
    maskImage: 'radial-gradient(110% 80% at 50% 0%, black 0%, transparent 78%)',
    WebkitMaskImage: 'radial-gradient(110% 80% at 50% 0%, black 0%, transparent 78%)',
};

// Deliberately no mix-blend-mode: on a fixed full-screen layer it creates a
// stacking context and forces a compositing pass every frame. Plain opacity
// gets almost all of the look for none of the cost, and this is the first
// layer to drop if frames ever suffer.
const GRAIN = {
    backgroundImage:
        `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E` +
        `%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E` +
        `%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
};

export default function AmbientCanvas() {
    return (
        <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
            {/* Base falloff — lighter at the top, so surfaces read as lit from
                above. Deliberately lighter than the surfaces that sit on it:
                the panes are dark cut-outs against a lit backdrop, which is
                what makes the gap between them read as depth rather than void. */}
            <div className="absolute inset-0 bg-[radial-gradient(125%_105%_at_50%_-5%,hsl(240_11%_14%)_0%,hsl(240_11%_8%)_45%,hsl(240_12%_5%)_100%)]" />
            {/* Project-tinted wash */}
            <div className="absolute inset-0 opacity-[0.75]" style={MESH} />
            {/* Faint structure in the empty space */}
            <div className="absolute inset-0 opacity-[0.5]" style={GRID} />
            {/* Grain — kills the banding these low-alpha gradients otherwise show */}
            <div className="absolute inset-0 opacity-[0.16]" style={GRAIN} />
        </div>
    );
}
